import { envConfig } from '#constants/envConfig';

/**
 * Куда вести человека, которому не отдали микрофон.
 *
 * <p>Совет «разрешите доступ в браузере» одинаково бесполезен на айфоне (там разрешение прячется
 * за «аА» в адресной строке, а не за замком) и прямо врёт в приложении, где браузера нет вовсе —
 * там путь лежит через системные настройки. Поэтому текст выбирается по среде, а не один на всех.
 */
export type MicDenialTarget =
  | 'native-android'
  | 'native-ios'
  | 'ios-browser'
  | 'android-browser'
  | 'desktop-browser';

const isIos = (ua: string, platform: string, touchPoints: number) =>
  /iPad|iPhone|iPod/.test(ua) ||
  // iPadOS 13+ представляется маком, и отличить его можно только по тачу.
  (platform === 'MacIntel' && touchPoints > 1);

const isAndroid = (ua: string) => /Android/.test(ua);

export const resolveMicDenialTarget = (): MicDenialTarget => {
  if (!envConfig.IN_BROWSER) {
    return 'desktop-browser';
  }

  const ua = navigator.userAgent ?? '';
  const platform = navigator.platform ?? '';
  const touchPoints = navigator.maxTouchPoints ?? 0;
  const apple = isIos(ua, platform, touchPoints);

  if (envConfig.IS_NATIVE) {
    return apple ? 'native-ios' : 'native-android';
  }
  if (apple) {
    return 'ios-browser';
  }
  return isAndroid(ua) ? 'android-browser' : 'desktop-browser';
};

export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * Статус разрешения на микрофон ДО попытки его захватить — чтобы предупредить человека до входа
 * в разговор, а не после, когда собеседник уже ждёт.
 *
 * <p>Ответу можно верить только в одну сторону. `denied` — сигнал надёжный: разрешения точно нет.
 * А `granted` и `prompt` доверять нельзя: Safari этого API не имеет вовсе (вернём `unknown`), а на
 * Android ≤12 системная проверка разрешений врёт — мы это уже проходили на пушах
 * (см. заметку про native push status). Поэтому фактическая попытка захвата остаётся источником
 * истины при любом ответе, а этот запрос нужен исключительно для раннего предупреждения.
 */
export const queryMicPermission = async (): Promise<MicPermission> => {
  if (!envConfig.IN_BROWSER || typeof navigator === 'undefined' || !navigator.permissions) {
    return 'unknown';
  }

  try {
    const status = await navigator.permissions.query({
      // В lib.dom `PermissionName` не содержит 'microphone', хотя браузеры его поддерживают.
      name: 'microphone' as PermissionName,
    });
    return status.state === 'denied' || status.state === 'granted' || status.state === 'prompt'
      ? status.state
      : 'unknown';
  } catch {
    // Safari бросает TypeError на неизвестное имя разрешения — это не ошибка, а «не знаю».
    return 'unknown';
  }
};
